import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { Add, Remove } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  SxProps,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { useFormik } from "formik";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { useTranslation } from "react-i18next";
import TiktokPixel from "tiktok-pixel";
import * as Yup from "yup";
import {
  useAddFavoriteActivityToList,
  useCreateFavoriteLists,
  useGetFavoriteTravelerActivityList,
  useRemoveFavoriteActivity,
} from "@/api/traveler";
import { CustomDialog } from "@/common/customDialog";
import CustomButton from "@/common/customElements/button";
import HexagonInput from "@/common/customElements/HexagonInput";
import { HorizontalCenterDiv } from "@/common/divs";
import { CustomInput } from "@/common/inputs";
import { RegularText, SemiBoldText } from "@/common/texts";
import { FavoriteIcon } from "@/components/icons";
import FirebaseContext from "@/context/firebaseContext/firebaseContext";
import { SnackbarContext } from "@/context/SnackbarContext";
import { useTrack } from "@/hooks/useTrack";
import { ActivityDetailGeoSearch } from "@/model";
import { FirebaseContextType } from "@/types/components";
import {
  NEUTRO_OVERLINE_COLOR,
  NEUTRO_TITLES_COLOR,
  PRIMARY_BRAND_VARIANT_COLOR,
  PRIMARY_BUTTON_COLOR,
  SECONDARY_BUTTON_COLOR,
} from "@/utils/constants";

export const FavoriteButton = ({
  sx = {},
  id: id,
  isFavoriteActivity,
  setFlagRemoved,
  activity,
}: {
  sx?: SxProps<Theme>;
  handleClick?: () => void;
  setFlagRemoved?: Dispatch<SetStateAction<boolean>>;
  id: number;
  isFavoriteActivity: boolean;
  activity: ActivityDetailGeoSearch;
}) => {
  const [open, setOpen] = useState(false);
  const [addListEnabled, setAddListEnabled] = useState(false);
  const [checkedId, setCheckedId] = useState<number>(0);
  const [isFavorite, setIsFavorite] = useState<boolean>(isFavoriteActivity);
  const { t } = useTranslation();
  const snackbarContext = useContext(SnackbarContext);
  const { mutateAsync: createFavoriteList, isLoading } =
    useCreateFavoriteLists();
  const session = useSession();
  const { data: favoritesActivityList } = useGetFavoriteTravelerActivityList(
    {},
    {
      query: {
        enabled: session.data?.user ? true : false,
        refetchOnWindowFocus: false,
      },
    },
  );
  const { mutateAsync: addFavoriteActivityToList } =
    useAddFavoriteActivityToList();
  const { mutateAsync: removeFavoriteActivity } = useRemoveFavoriteActivity();
  const isMobile = useMediaQuery("(max-width:850px)");
  const formik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("El campo es obligatorio")),
    }),
    onSubmit: async (values) => {
      TiktokPixel.track("Add to Favorites", {
        contents: [
          {
            content_id: id,
            content_name: activity.name,
            content_category: activity.category.name,
          },
        ],
        activity_id: id,
        activity_name: activity.name,
        activity_category: activity.category.name,
        activity_subcategory: activity.subcategory?.name,
      });
      createFavoriteList({ data: { name: values.name } }).then(() => {});
    },
  });

  const { trackEvent, TrackCustomEvents } = useTrack();
  const { logAnalyticsEvent } = useContext(
    FirebaseContext,
  ) as FirebaseContextType;

  const listsFavoritesActivities = useMemo(() => {
    return favoritesActivityList ? favoritesActivityList.content : [];
  }, [favoritesActivityList]);

  const actionsButtons = useMemo(() => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          flexDirection: isMobile ? "column" : "row",
          gap: "32px",
          width: "100%",
        }}
      >
        <CustomButton
          width={isMobile ? "100%" : "318px"}
          backgroundColor={SECONDARY_BUTTON_COLOR}
          height="50px"
          borderColor={PRIMARY_BUTTON_COLOR}
          onClick={() => {
            setOpen(false);
          }}
        >
          <RegularText fontSize="18px" color={NEUTRO_TITLES_COLOR}>
            {t("Cancelar")}
          </RegularText>
        </CustomButton>
        <CustomButton
          width={isMobile ? "100%" : "318px"}
          backgroundColor={PRIMARY_BUTTON_COLOR}
          height="50px"
          borderColor={PRIMARY_BUTTON_COLOR}
          onClick={async () => {
            await addFavoriteActivityToList({
              id: checkedId,
              data: { activityId: id },
            })
              .then(() => {
                setIsFavorite(true);
                snackbarContext.show(
                  {
                    ...snackbarContext.state,
                    open: true,
                  },
                  t("Guardado en tu lista de favoritos"),
                  "success",
                  undefined,
                  <Link
                    href={"/profile/favorites"}
                    style={{ marginLeft: "16px" }}
                  >
                    Ver Favoritos
                  </Link>,
                );
                trackEvent({
                  name: TrackCustomEvents.EVENT_ADD_TO_WISHLIST,
                  properties: {
                    activity_name: activity.name,
                    activity_id: id,
                    activity_category: activity.category,
                    activity_subcategory: activity.subcategory,
                  },
                });
                logAnalyticsEvent(TrackCustomEvents.EVENT_ADD_TO_WISHLIST_WEB, {
                  activity_name: activity.name,
                  activity_id: id,
                  activity_category: activity.category,
                  activity_subcategory: activity.subcategory,
                });
                setTimeout(() => {
                  snackbarContext.close();
                }, 5000);
              })
              .catch((error) => {
                snackbarContext.show(
                  {
                    ...snackbarContext.state,
                    open: true,
                  },
                  error.response.data.message.replaceAll(".", " "),
                  "error",
                );
                setTimeout(() => {
                  snackbarContext.close();
                }, 5000);
              });
            setOpen(false);
          }}
        >
          <RegularText fontSize="18px" color={NEUTRO_TITLES_COLOR}>
            {t("Guardar como favorito")}
          </RegularText>
        </CustomButton>
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, checkedId, id, addFavoriteActivityToList, snackbarContext]);

  return (
    <>
      <IconButton
        aria-label="favorite activity"
        sx={{ ...sx }}
        onClick={async () => {
          if (isFavorite) {
            await removeFavoriteActivity({ activityId: id })
              .then(() => {
                setIsFavorite(false);
                if (setFlagRemoved) {
                  setFlagRemoved(true);
                }
                snackbarContext.show(
                  {
                    ...snackbarContext.state,
                    open: true,
                  },
                  t("Eliminado de tu lista de favoritos"),
                  "success",
                  undefined,
                );

                setTimeout(() => {
                  snackbarContext.close();
                }, 5000);
              })
              .catch((error) => {
                snackbarContext.show(
                  {
                    ...snackbarContext.state,
                    open: true,
                  },
                  error.response.data.message.replaceAll(".", " "),
                  "error",
                );
                setTimeout(() => {
                  snackbarContext.close();
                }, 5000);
              });
          } else {
            setOpen(true);
          }
        }}
      >
        <FavoriteIcon
          sx={{
            width: "32px",
            height: "32px",
            "&:hover #svg-icon": {
              stroke: PRIMARY_BUTTON_COLOR,
            },
            "& #svg-icon": {
              stroke: PRIMARY_BRAND_VARIANT_COLOR,
              fill: isFavorite ? PRIMARY_BUTTON_COLOR : "unset",
            },
          }}
        ></FavoriteIcon>
      </IconButton>
      <CustomDialog
        handleClose={() => {
          setOpen(false);
        }}
        isLoading={false}
        open={open}
        title={t("Agregar a favoritos")}
        actions={actionsButtons}
        sx={{
          minWidth: {
            xs: "80vw",
            sm: "600px",
            md: "800px",
          },
        }}
      >
        <Divider></Divider>
        <Grid container>
          {listsFavoritesActivities?.map((listItem, index) => (
            <Grid
              item
              key={index}
              xs={12}
              sx={{ marginTop: "24px", display: "flex" }}
            >
              <HexagonInput
                checked={checkedId == listItem.id}
                onClick={() => {
                  setCheckedId(listItem.id);
                }}
              ></HexagonInput>
              {/*{JSON.stringify(listItem)}*/}
              <RegularText
                fontSize={"16px"}
                style={{ display: "flex", marginLeft: "12px" }}
              >
                {t(listItem.name)}
              </RegularText>
            </Grid>
          ))}
          <Grid item xs={12} sx={{ marginTop: "24px", display: "flex" }}>
            {addListEnabled ?
              <Remove
                sx={{ color: NEUTRO_OVERLINE_COLOR }}
                onClick={() => {
                  setAddListEnabled(!addListEnabled);
                }}
              />
            : <Add
                sx={{ color: NEUTRO_OVERLINE_COLOR }}
                onClick={() => {
                  setAddListEnabled(!addListEnabled);
                }}
              />
            }

            <RegularText fontSize={"16px"} style={{ marginLeft: "12px" }}>
              {t("Crear nueva lista de favoritos")}
            </RegularText>
          </Grid>
          {addListEnabled ?
            <>
              <Grid item xs={12} sx={{ marginTop: "24px" }}>
                <RegularText
                  fontSize={"16px"}
                  style={{ display: "flex", marginBottom: "8px" }}
                >
                  {t("Asigna un nombre a tu lista")}
                </RegularText>
              </Grid>
              <HorizontalCenterDiv
                style={{
                  justifyContent: "space-between",
                  gap: "10px",
                  width: "100%",
                  flexDirection: window.innerWidth < 500 ? "column" : "row",
                }}
              >
                <Grid item xs={window.innerWidth < 500 ? 12 : 6}>
                  <CustomInput
                    placeholder={t("Nombre de la lista")}
                    {...formik.getFieldProps("name")}
                    width={"100%"}
                  />
                </Grid>
                <Grid
                  item
                  xs={window.innerWidth < 500 ? 12 : 6}
                  display={"flex"}
                  justifyContent={"right"}
                  style={{ width: window.innerWidth < 500 ? "100%" : "auto" }}
                >
                  {isLoading ?
                    <HorizontalCenterDiv width="100%" paddingY="26px">
                      <CircularProgress color="primary" size={24} />
                    </HorizontalCenterDiv>
                  : <div
                      style={{
                        width: window.innerWidth < 500 ? "100%" : "auto",
                      }}
                    >
                      <CustomButton
                        onClick={formik.handleSubmit}
                        height="42px"
                        width={window.innerWidth < 500 ? "100%" : "300px"}
                        backgroundColor={PRIMARY_BUTTON_COLOR}
                      >
                        <SemiBoldText
                          fontSize="16px"
                          color={NEUTRO_TITLES_COLOR}
                        >
                          {t("Guardar lista")}
                        </SemiBoldText>
                      </CustomButton>
                    </div>
                  }
                </Grid>
              </HorizontalCenterDiv>
            </>
          : null}
        </Grid>
      </CustomDialog>
    </>
  );
};
